import { KeyboardEvent, MouseEvent } from 'react';
import { POLineGRNSummaryRow } from '../../lib/api';
import { getAppealStatusBadge, getOntimeStatusBadge } from './tabs/delivery/deliveryBadges';
import { isQuantityOnlyAppeal } from './tabs/delivery/appealEligibility';
import { Calendar, Hash, ExternalLink } from 'lucide-react';
import { acceptedOf, ncrTooltip, oddOf, rejectedOf } from './poLineGrnRows';

interface POLineGRNRowProps {
  grn: POLineGRNSummaryRow;
  index: number;
  /** CR-7265: schedule-grain tenants only. */
  showScheduleLine: boolean;
  /**
   * CR-7415: ODD, On-time Status and Appeal Status, and the row opening the
   * delivery. Off at schedule grain, where the verdict is per schedule line.
   */
  showVerdicts: boolean;
  formatDate: (dateString: string) => string;
  formatQuantity: (value: string | number | null | undefined) => string;
  onOpenGRN: (grnNumber: string) => void;
  onOpenAllOrders: (grnNumber: string) => void;
  /** CR-7415: open the Delivery Details window for this delivery. */
  onOpenDelivery: (grn: POLineGRNSummaryRow) => void;
}

/**
 * One receipt in the PO line GRN tab's Delivery History.
 *
 * Lifted out of POLineGRNTab when CR-7415 added the verdict columns (500-line
 * limit). At PO-line grain the row also opens the Delivery Details window the
 * Deliveries page uses, so a delivery can be appealed or acknowledged without
 * leaving the PO line; the GRN number still opens the Deliveries page.
 *
 * @param props - See {@link POLineGRNRowProps}.
 * @returns The table row.
 */
export default function POLineGRNRow({
  grn, index, showScheduleLine, showVerdicts, formatDate, formatQuantity,
  onOpenGRN, onOpenAllOrders, onOpenDelivery,
}: POLineGRNRowProps) {
  // The links in the first cell keep their own destination: they must not
  // also open the window underneath them.
  const linkClick = (open: (n: string) => void) => (e: MouseEvent) => {
    e.stopPropagation();
    open(grn.grn_number);
  };
  // Enter on the focused row, not Enter bubbling up from one of its links.
  const onKeyDown = (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' && e.target === e.currentTarget) onOpenDelivery(grn);
  };
  const odd = oddOf(grn);
  // Same derivation as the Deliveries grid's row (DeliveryRow), so the Appeal
  // Status reads the same in both places.
  const quantityOnly = isQuantityOnlyAppeal(grn) && grn.can_appeal === true;

  return (
    <tr
      className={`hover:bg-muted/30 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-muted/10'}${showVerdicts ? ' cursor-pointer' : ''}`}
      {...(showVerdicts ? {
        onClick: () => onOpenDelivery(grn),
        onKeyDown,
        tabIndex: 0,
        title: 'Open delivery details',
      } : {})}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Hash className="h-4 w-4 text-muted-foreground" />
          <button
            onClick={linkClick(onOpenGRN)}
            className="font-medium text-primary hover:underline inline-flex items-center gap-1"
            title="View this GRN on Deliveries page"
          >
            {grn.grn_number}
            <ExternalLink className="h-3 w-3" />
          </button>
          {/* CR-7256: only offer "View All" when the number has a
              suffix to strip. Without one it searches the same term
              as the number beside it and opens an identical page —
              the redundant second control reported on the staging
              walkthrough. */}
          {grn.grn_number.includes('-') && (
            <button
              onClick={linkClick(onOpenAllOrders)}
              className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
              title="View all lines on this delivery"
            >
              View All
            </button>
          )}
        </div>
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">{formatDate(grn.receipt_date)}</span>
        </div>
      </td>
      {showVerdicts && (
        <>
          <td className="px-4 py-3 text-sm whitespace-nowrap">{odd ? formatDate(odd) : '-'}</td>
          {/* The badge the Delivery Details window draws (CR-7418: one definition). */}
          <td className="px-4 py-3 whitespace-nowrap">
            {grn.on_time_status
              ? getOntimeStatusBadge(grn.on_time_status, grn.otif_overridden || grn.otif_acknowledged, grn.on_time_status_display, grn.allocations)
              : '-'}
          </td>
          <td className="px-4 py-3 whitespace-nowrap">
            {getAppealStatusBadge(grn.latest_appeal?.status || 'none', grn.latest_appeal?.is_customer_appeal, grn.workflow_status, grn.on_time_status, quantityOnly)}
          </td>
        </>
      )}
      {showScheduleLine && (
        <td className="px-4 py-3 text-right text-sm">{grn.schedule_line_number ?? '-'}</td>
      )}
      <td className="px-4 py-3 text-right">
        <span className="font-medium">{formatQuantity(grn.quantity_received)}</span>
      </td>
      <td className="px-4 py-3 text-right">
        <span className="font-medium text-green-700">{formatQuantity(acceptedOf(grn))}</span>
      </td>
      <td className="px-4 py-3 text-right">
        <span
          className={`font-medium text-red-700 ${grn.ncr_refs?.length ? 'underline decoration-dotted cursor-help' : ''}`}
          title={ncrTooltip(grn.ncr_rejected_qty, grn.ncr_refs)}
        >
          {formatQuantity(rejectedOf(grn))}
        </span>
      </td>
      <td className="px-4 py-3">
        <span className="text-sm text-muted-foreground">
          {grn.packing_slip_number || '-'}
        </span>
      </td>
    </tr>
  );
}
